import axios from 'axios';
import ProductGrid from './ProductGrid';
import ProductSpecs from './ProductSpecs';
import YoutubeEmbed from '../../components/shared/YoutubeEmbed';
import Modal from 'react-responsive-modal';
import { useState } from 'react';
import 'react-responsive-carousel/lib/styles/carousel.min.css'; // requires a loader
import TruncatedText from '../shared/TruncatedText';
import Breadcrumbs from '../shared/Breadcrumbs';
import Link from 'next/link';
import EmailNotVerifiedMessage from '../shared/EmailNotVerifiedMessage';
import { ApplicationState } from '../../types/state/storeTypes';
import { connect } from 'react-redux';
import RelatedCollectionSliderContainer from './RelatedCollectionSlider';
import { productMedia as pm } from '../../staticData/ProductMedia';
import { userIsNotConfirmed } from '../../helpers/user';
import CustomCarouselWithThumbs from '../../modules/CustomCarouselWithThumbs';
import DownloadLimitNotification from '../downloads/DownloadLimitNotification';
import RelatedCollectionsSection from './RelatedCollectionsSection';

function PFLCollectionPage(props) {
  const { collection, products, product, ownership, filters, user, plans, content } = props;
  const [videoModal, setVideoModal] = useState(null);

  const filterCount = (name) => {
    return filters.filter((f) => f.option_value == name)[0]?.pfl_usage_count || '';
  };

const productMedia = (collection, pm) => {
  if (!collection || !pm) {
    return [];
  }

  return [
    {
      type: 'video',
      subtype: 'featured_video',
      url: collection.media.mp4,
      image: collection.media.image
    },
    ...(pm[collection.sku] || [])
  ];
}

const shoppeProductMedia = (collection) => {
  if (!collection || !collection.product_media) {
    return [];
  }

  return collection.product_media.sort((a, b) => a.position - b.position).map((mediaItem) => {
    const isFeaturedVideo = mediaItem.video_url !== '';

    return {
      type: 'video',
      subtype: isFeaturedVideo ? 'featured_video' : 'youtube',
      url: isFeaturedVideo ? mediaItem.video_url : mediaItem.youtube_link,
      image: '',
    };
  });
};

const combineMedia = (collection, pm) => {
  if (!collection || !collection.media || !collection.product_media || !collection.sku) {
    return [];
  }

  const productMediaArray = productMedia(collection, pm);
  const shoppeProductMediaArray = shoppeProductMedia(collection);
  const combinedMedia = [...productMediaArray, ...shoppeProductMediaArray];

  return combinedMedia;
};

const combinedMedia = combineMedia(collection, pm);

  const carouselOptions = {
    dots: false,
    infinite: true,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: false,
    arrows: false,
    fade: true,
    speed: 300,
    overflow: 'hidden'
  };

  const filterOptions = () => {
    const opts = collection.optionables;
    return opts.filter((o) => {
      return ['effectType', 'technique', 'cameraMovement', 'timeOfDay', 'genre', 'people'].includes(
        o.type
      );
    });
  };

  return (
    <>
      <div className="bg-a-darker-gray text-white">
        <div className="flex">
          <div
            id="collection-page"
            className="w-full  md:w-[calc(100%-380px)] lg:w-[calc(100%-400px)] 2xl:w-[calc(100%-440px)] bg-[#252A2D]"
          >
            <Breadcrumbs
              className="hidden md:block p-4 md:p-8"
              items={[
                { label: 'Practice Footage Library', url: '/practice-footage' },
                { label: collection.name }
              ]}
            />
            <div className="flex flex-col lg:flex-row md:px-8 gap-8 4k:gap-16">
              <div className="lg:w-1/2 max-w-[800px] product-gallery-section">
                {combinedMedia && (
                  <CustomCarouselWithThumbs
                    carouselOptions={carouselOptions}
                    content={combinedMedia}
                  />
                )}
              </div>
              <div id="product-features-section" className="px-6 md:px-0 lg:w-1/2 4k:pr-16">
                <div className="flex items-start">
                  <h1 className="font-[Inter] text-[24px] md:text-[40px] font-bold leading-tight pb-4">
                    {collection.name}
                  </h1>
                </div>

                <p className="text-a-gray text-[14px] lg:text-[14px] xl:text-[16px] leading-[24px] font-normal pb-4 whitespace-pre-line">
                  <TruncatedText text={collection.description} />
                </p>

                <ProductSpecs collection={collection} />

                <div className="w-full mt-4">
                  {filterOptions().map((optionable, index) => (
                    <a
                      key={index}
                      className="inline-block mb-3 px-4 py-2 bg-[rgb(255,255,255)] bg-opacity-10 hover:bg-opacity-[.15] mr-2 rounded-3xl text-[#AEB6BA] text-[14px] whitespace-nowrap"
                      href={`/practice-footage?options=${optionable.option_value_id}`}
                    >
                      <span>{optionable.value}</span>
                      <span className="text-gray-600 pl-2 text-[12px]">
                        {filterCount(optionable.value)}
                      </span>
                    </a>
                  ))}
                </div>
              </div>
            </div>

            <div className="block md:hidden my-8">
              <div className="p-6 bg-[#1C1F20]">
                <p className="text-a-light-gray uppercase text-sm pb-2">SCENE {collection.sku}</p>
                <p className="text-white text-2xl font-bold">{collection.name}</p>
              </div>
            </div>

            <div className="p-4 md:px-8">
              <div className="md:flex gap-4 align-middle items-center h-[64px]">
                <h3 className="font-bold text-[18px] md:text-[20px] inter">
                  {collection.elements} Clips
                </h3>
                <p className="text-a-light-gray text-[12px] font-medium">
                  Download clips individually below
                </p>
              </div>

              <div className="py-4">
                <ProductGrid
                  products={products}
                  collection={collection}
                  selectedProduct={product}
                  ownership={ownership}
                  isPfl={true}
                />
              </div>

              <RelatedCollectionsSection collectionId={collection.id} />
            </div>
          </div>

          <div className="hidden md:block 4k:w-[960px] 2xl:w-[700px] xl:w-[550px] md:w-[400px] min-w-[380px] p-8 pt-12 bg-[#1C1F20]">
            <div className="hidden md:block sticky top-[7rem]">
              <p className="text-a-light-gray uppercase text-[16px] pb-2">SCENE {collection.sku}</p>
              <p className="text-white text-[40px] font-bold leading-tight">{collection.name}</p>

              {/* IF CONTRIBUTOR  */}

              {collection.contributor && (
                <div className="mt-8">
                  <div className="mb-4 leading-[24px] text-[16px] tracking-wide font-light">
                    Author:
                  </div>

                  <Link href={`/c/${collection.contributor.username}`} className="flex gap-2">
                    <img
                      className="w-[24px] h-[24px] rounded-full"
                      src={collection.contributor.avatar}
                      alt="ContributorProfilePic"
                      loading="lazy"
                    />
                    <p className="text-a-blue font-leading-22 text-[16px]">
                      {collection.contributor.name}
                    </p>
                  </Link>
                </div>
              )}

              <p className="mt-8 text-[16px] text-white font-light">Licensing:</p>
              <ul className="px-6 py-2 list-disc text-[14px] font-light text-gray-500 tracking-wide">
                <li className="py-1">
                  Fully commercial license in accordance with the ActionVFX{' '}
                  <a href="/license" className="text-a-blue">
                    License Agreement
                  </a>
                </li>
                <li className="py-1">Use for learning, teaching, your demo reel, and more</li>
              </ul>

              <h4 className="mt-8 font-bold text-[#24C1FF] text-[28px] inter leading-28">
                AVAILABLE FOR FREE
              </h4>
              <p className="text-gray-600 text-[14px] font-light leading-22 mt-1 tracking-wide">
                To save on data transfer costs, downloads are limited to 10 files per day per user.
              </p>
              {userIsNotConfirmed(user) && (
                <p className="text-gray-600 text-[14px] mt-8">
                  <EmailNotVerifiedMessage />
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {videoModal && (
        <Modal
          classNames={{ modal: '!p-0 !bg-transparent !m-0 w-[100%] lg:w-[800px]' }}
          open={videoModal != null}
          onClose={() => setVideoModal(null)}
          center
          showCloseIcon={false}
        >
          <YoutubeEmbed embedId={videoModal.video_key} className="bg-black" />
        </Modal>
      )}
      <DownloadLimitNotification />
    </>
  );
}

export const getServerSideProps = async ({ params }) => {
  const slug = params.slug;
  const apiUrl = process.env.API_URL;

  const collectionsUrl = `${apiUrl}/collections`;
  const collectionParams = {
    order_by: 'position',
    desc: false,
    page: 1
  };
  const collectionsRes = await axios.get(collectionsUrl + '/' + slug, {
    params: collectionParams,
    headers: {}
  });

  const collection = collectionsRes.data;

  const productsRes = await axios.get(`${apiUrl}/collections/${collection.id}/products`, {
    headers: {}
  });

  return {
    props: {
      collection: collection,
      products: productsRes.data
    }
  };
};

const mapStateToProps = (state: ApplicationState) => ({
  user: state.auth.user
});

export default connect(mapStateToProps)(PFLCollectionPage);
